/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.

This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.

License Agreement:
By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.

This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.

For inquiries or requests regarding this software, please contact eWorldES at:
Email: info@eworldes.com
Website: https://eworldes.com/
*/
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import { Controller } from "react-hook-form";
import styled from "styled-components";
import { InputFieldProps } from "../../types/InputProps";
import InputWrapper from "./InputWrapper";
import { InputAdornment } from "../InputAdornment";
import { ICON_MAP } from "../MUIIcons/Icons";

const TextStyle = styled(TextField)`
  input {
    height: 39px;
  }
  label {
    line-height: 2.5;
  }
  .MuiInputLabel-shrink {
    line-height: 1.52;
  }
`;

const FormInputText = (props: InputFieldProps) => {
  const { name, control, info, hint, meta, validations = [] } = props;

  const {
    startAdornmentIcon,
    endAdornmentIcon,
    InputProps = {},
    ...restMeta
  } = meta;

  const getIconComponent = (iconKey?: string) => {
    if (!iconKey) return null;
    const Component = ICON_MAP[iconKey as keyof typeof ICON_MAP];
    return Component ? <Component /> : null;
  };

  const startAdornment = getIconComponent(startAdornmentIcon);
  const endAdornment = getIconComponent(endAdornmentIcon);

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        validate: (val) => val.trim().length > 0 || "Spaces alone are not allowed"
      }}
      
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <InputWrapper info={info} hint={hint} error={error}>
          <FormControl fullWidth>
            <TextStyle
              required={validations.length > 0}
              variant="outlined"
              onChange={onChange}
              onBlur={(e) => {
                onChange(e.target.value.trim());
                props.onBlur?.(e);
              }}
              label={props.label}
              autoFocus={props.autoFocus}
              value={value}
              error={!!error}
              InputProps={{
                startAdornment: startAdornment ? (
                  <InputAdornment position="start">
                    {startAdornment}
                  </InputAdornment>
                ) : InputProps.startAdornment,
                endAdornment: endAdornment ? (
                  <InputAdornment position="end">
                    {endAdornment}
                  </InputAdornment>
                ) : InputProps.endAdornment,
                ...InputProps,
              }}
              {...restMeta}
            />
          </FormControl>
        </InputWrapper>
      )}
    />
  );
};

export default FormInputText;
